import axios, { AxiosInstance, AxiosResponse } from 'axios';

// ─────────────────────────────────────────────
// Base URL
// ─────────────────────────────────────────────
// In production the build sets this to '' so all /api calls hit the same
// origin and nginx proxies them to the backend.  In local dev it points
// directly at the backend dev server on port 8000.
export const BASE_URL: string =
  process.env.REACT_APP_API_URL !== undefined
    ? process.env.REACT_APP_API_URL
    : 'http://localhost:8000';

// ─────────────────────────────────────────────
// JWT token – stored in memory only (not localStorage)
// ─────────────────────────────────────────────
let _token: string | null = null;

export function setToken(token: string | null): void {
  _token = token;
}

export function getToken(): string | null {
  return _token;
}

// ─────────────────────────────────────────────
// Axios instance
// ─────────────────────────────────────────────
const api: AxiosInstance = axios.create({ baseURL: BASE_URL });

// Request interceptor – attach Bearer token
api.interceptors.request.use((config) => {
  if (_token) {
    config.headers = config.headers ?? {};
    config.headers['Authorization'] = `Bearer ${_token}`;
  }
  return config;
});

// Response interceptor – redirect to /login on 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      setToken(null);
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ─────────────────────────────────────────────
// TypeScript interfaces
// ─────────────────────────────────────────────

export interface SetupData {
  username: string;
  password: string;
  capital_api_key: string;
  capital_identifier: string;
  capital_password: string;
  account_type: 'LIVE' | 'DEMO';
  telegram_token?: string;
  telegram_chat_id?: string;
}

export interface HistoryParams {
  page?: number;
  page_size?: number;
  symbol?: string;
  direction?: 'LONG' | 'SHORT';
  date_from?: string;
  date_to?: string;
  outcome?: 'WIN' | 'LOSS';
}

export interface UserMe {
  id: number;
  username: string;
  email?: string;
}

export interface SetupStatus {
  configured: boolean;
}

export interface DashboardData {
  account: {
    equity: number;
    available: number;
    margin: number;
    currency: string;
  };
  open_trades: {
    count: number;
    winning: number;
    losing: number;
    total_pnl: number;
  };
  history: {
    total_closed: number;
    win_rate: number;
    total_pnl: number;
  };
  notifications: {
    unread_count: number;
  };
  market_status: {
    US: boolean;
    UK: boolean;
    EU: boolean;
    AU: boolean;
    JP: boolean;
  };
  new_watchlist_items: Array<{ id: number; symbol: string; name: string }>;
  backfill_progress?: {
    in_progress: boolean;
    current: number;
    total: number;
  };
  // Kibar sections (optional — absent when Kibar client not configured)
  kibar_account?: {
    equity: number;
    available: number;
    margin: number;
    currency: string;
    accountName?: string;
  };
  kibar_open_trades?: {
    count: number;
    winning: number;
    losing: number;
    total_pnl: number;
  };
  kibar_watchlist?: {
    total: number;
    trading_enabled: number;
  };
  kibar_history?: {
    total_closed: number;
    win_rate: number;
    total_pnl: number;
  };
}

export interface KibarItem {
  id: number;
  epic: string;
  name?: string;
  asset_type?: string;
  market_region: 'US' | 'EU_UK' | 'JP_AU';
  trade_direction: 'BUY' | 'SELL';
  direction: 'LONG' | 'SHORT';
  support_resistance_level: number;
  breakout_level: number;
  price_target: number;
  stop_loss: number;
  stop_loss_level: number;
  target_level: number;
  rr_ratio: number;
  trade_enabled: boolean;
  is_trading_enabled: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface WatchlistItem {
  id: number;
  epic: string;
  name?: string;
  asset_type?: string;
  direction: 'LONG' | 'SHORT';
  timeframe: string;
  breakout_level: number;
  stop_loss_level: number;
  target_level: number;
  rr_ratio: number;
  is_trading_enabled: boolean;
  is_new: boolean;
  added_at?: string;
  support_level?: number;
  resistance_level?: number;
  // aliases used in Watchlist.tsx
  support?: number;
  resistance?: number;
  ema200_position?: string;
  duration_days?: number;
  duration_months?: number;
  current_price?: number;
  price_change_pct?: number;
}

export interface Trade {
  id: number | string;
  symbol: string;
  epic: string;
  direction: 'LONG' | 'SHORT';
  size: number;
  entry_price: number;
  current_price: number;
  stop_loss?: number;
  take_profit?: number;
  trailing_stop?: number;
  open_pnl: number;
  open_time: string | null;
  deal_id?: string;
  is_manual?: boolean;
  instrument_name?: string;
}

export interface HistoryTrade {
  id: number;
  symbol?: string;
  epic: string;
  instrument_name?: string;
  asset_type?: string;
  direction: 'LONG' | 'SHORT';
  size?: number;
  position_size_units?: number;
  entry_price: number;
  exit_price?: number | null;
  stop_loss?: number;
  take_profit?: number;
  target_price?: number;
  pnl: number;
  pnl_total?: number;
  outcome: 'WIN' | 'LOSS';
  rr_ratio?: number;
  open_time?: string;
  close_time?: string;
  entry_time?: string;
  exit_time?: string;
  close_reason?: string;
  exit_reason?: string;
  duration_hours?: number;
}

export interface HistoryStats {
  total_trades: number;
  wins: number;
  losses: number;
  win_rate: number;
  total_pnl: number;
  average_win: number;
  average_loss: number;
  profit_factor: number;
}

export interface BacktestResult {
  id: number;
  symbol: string;
  direction: 'LONG' | 'SHORT';
  entry_price: number;
  exit_price?: number;
  pnl?: number;
  outcome?: 'WIN' | 'LOSS';
  open_time: string;
  close_time?: string;
}

export interface BacktestSummary {
  trades: BacktestResult[];
  stats: HistoryStats;
  equity_curve: Array<{ time: string; value: number }>;
}

export interface Notification {
  id: number;
  title: string;
  message: string;
  level: 'INFO' | 'WARNING' | 'ERROR' | 'SUCCESS';
  is_read: boolean;
  created_at: string;
}

export interface NotificationsResponse {
  items: Notification[];
  total: number;
  unread_count: number;
}

export interface Setting {
  key: string;
  value: string;
  description?: string;
  category?: string;
}

export interface ChartCandle {
  time: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

export interface ChartOverlays {
  support?: number;
  resistance?: number;
  ema200?: Array<{ time: string; value: number }>;
  breakout_level?: number;
  stop_loss_level?: number;
  target?: number;
  entry_price?: number;
  exit_price?: number;
  trailing_stop?: number;
}

export interface ChartData {
  candles: ChartCandle[];
  overlays: ChartOverlays;
  timeframe?: string;
  chart_start?: string;  // YYYY-MM-DD: formation start date, used to set initial viewport
}

// ─────────────────────────────────────────────
// Auth endpoints
// ─────────────────────────────────────────────

export const login = (
  username: string,
  password: string
): Promise<AxiosResponse<{ access_token: string; token_type: string }>> =>
  api.post('/api/auth/login', { username, password });

export const setupAccount = (
  data: SetupData
): Promise<AxiosResponse<{ access_token: string; token_type: string }>> =>
  api.post('/api/auth/setup', data);

export const getSetupStatus = (): Promise<AxiosResponse<SetupStatus>> =>
  api.get('/api/auth/setup-status');

export const getMe = (): Promise<AxiosResponse<UserMe>> =>
  api.get('/api/auth/me');

// ─────────────────────────────────────────────
// Dashboard endpoints
// ─────────────────────────────────────────────

export const getDashboard = (): Promise<AxiosResponse<DashboardData>> =>
  api.get('/api/dashboard/');

// ─────────────────────────────────────────────
// Watchlist endpoints
// ─────────────────────────────────────────────

export const getWatchlist = (params?: {
  page?: number;
  page_size?: number;
  search?: string;
  direction?: string;
  asset_type?: string;
  trading_only?: boolean;
  trading_status?: 'yes' | 'no';
  rr_min?: number;
  rr_max?: number;
  sort_by?: string;
  sort_dir?: string;
  hot?: boolean;
}): Promise<AxiosResponse<any>> =>
  api.get('/api/watchlist/', { params });

export const toggleTrading = (
  id: number,
  enabled: boolean
): Promise<AxiosResponse<WatchlistItem>> =>
  api.patch(`/api/watchlist/${id}/trading`, { is_trading_enabled: enabled });

export const dismissAllNew = (): Promise<AxiosResponse<{ ok: boolean }>> =>
  api.post('/api/watchlist/dismiss-all-new');

export const dismissNew = (
  id: number
): Promise<AxiosResponse<WatchlistItem>> =>
  api.patch(`/api/watchlist/${id}/dismiss-new`);

export const getWatchlistChartData = (
  id: number
): Promise<AxiosResponse<ChartData>> =>
  api.get(`/api/watchlist/${id}/chart-data`);

export const sendWatchlistTelegram = (id: number, image?: string | null): Promise<AxiosResponse<any>> =>
  api.post(`/api/watchlist/${id}/send-telegram`, { image: image ?? null });

export const exportWatchlist = (): void => {
  window.open(`${BASE_URL}/api/watchlist/export?token=${getToken()}`);
};

export const updateWatchlistLevels = (
  id: number,
  data: { support_level: number; resistance_level: number; direction: 'LONG' | 'SHORT' }
): Promise<AxiosResponse<WatchlistItem>> =>
  api.patch(`/api/watchlist/${id}/levels`, data);

// ─────────────────────────────────────────────
// Open Trades endpoints
// ─────────────────────────────────────────────

export const getOpenTrades = (): Promise<AxiosResponse<Trade[]>> =>
  api.get('/api/trades/');

export const closeTradeManual = (
  id: number
): Promise<AxiosResponse<{ success: boolean }>> =>
  api.post(`/api/trades/${id}/close`);

export const closeTradeByDealId = (data: {
  deal_id: string;
  epic: string;
  direction: string;
  size: number;
}): Promise<AxiosResponse<{ success: boolean; orders_cancelled: number }>> =>
  api.post('/api/trades/close-by-dealid', data);

export const getTradeChartData = (
  id: number
): Promise<AxiosResponse<ChartData>> =>
  api.get(`/api/trades/${id}/chart-data`);

// ─────────────────────────────────────────────
// History endpoints
// ─────────────────────────────────────────────

export const getHistory = (
  params: HistoryParams
): Promise<AxiosResponse<{ items: HistoryTrade[]; total: number }>> =>
  api.get('/api/history/', { params });

export const getHistoryStats = (
  params: HistoryParams
): Promise<AxiosResponse<HistoryStats>> =>
  api.get('/api/history/stats', { params });

export const getHistoryChartData = (
  id: number
): Promise<AxiosResponse<ChartData>> =>
  api.get(`/api/history/${id}/chart-data`);

// ─────────────────────────────────────────────
// Backtest endpoints
// ─────────────────────────────────────────────

export const runBacktest = (
  params?: Record<string, unknown>
): Promise<AxiosResponse<BacktestSummary>> =>
  api.post('/api/backtest/run', params, { timeout: 30 * 60 * 1000 }); // 30 min timeout

export const getBacktestResults = (): Promise<AxiosResponse<BacktestSummary>> =>
  api.get('/api/backtest/results');

// ─────────────────────────────────────────────
// Notifications endpoints
// ─────────────────────────────────────────────

export const getNotifications = (
  limit?: number,
  offset?: number
): Promise<AxiosResponse<NotificationsResponse>> =>
  api.get('/api/notifications/', { params: { limit, offset } });

export const markNotifRead = (
  id: number
): Promise<AxiosResponse<Notification>> =>
  api.patch(`/api/notifications/${id}/read`);

export const markAllRead = (): Promise<AxiosResponse<{ success: boolean }>> =>
  api.post('/api/notifications/mark-all-read');

// ─────────────────────────────────────────────
// Settings endpoints
// ─────────────────────────────────────────────

export const getSettings = (): Promise<AxiosResponse<Record<string, Setting>>> =>
  api.get('/api/settings/');

export const updateSettings = (
  settings: Record<string, string>
): Promise<AxiosResponse<Record<string, Setting>>> =>
  api.put('/api/settings/', settings);

export const resetSettings = (): Promise<AxiosResponse<Record<string, Setting>>> =>
  api.post('/api/settings/reset');

export const refreshPriceData = (): Promise<AxiosResponse<{ ok: boolean; message: string }>> =>
  api.post('/api/settings/refresh-prices');

export const rescanPatterns = (): Promise<AxiosResponse<{ ok: boolean; message: string }>> =>
  api.post('/api/settings/rescan-patterns');

export const refreshTradingHours = (): Promise<AxiosResponse<{ ok: boolean; message: string }>> =>
  api.post('/api/settings/refresh-trading-hours');

export interface DataStatusInstrument {
  epic: string;
  latest_candle: string | null;
  is_stale: boolean;
}

export interface DataStatus {
  last_update_started: string | null;
  last_update_finished: string | null;
  last_business_day: string;
  stale_instrument_count: number;
  instruments: DataStatusInstrument[];
}

export const getDataStatus = (): Promise<AxiosResponse<DataStatus>> =>
  api.get('/api/settings/data-status');

// ─────────────────────────────────────────────
// Kibar Watchlist endpoints
// ─────────────────────────────────────────────

export const getKibarWatchlist = (params?: any): Promise<AxiosResponse<any>> =>
  api.get('/api/kibar/', { params });

export const getKibarItem = (id: number): Promise<AxiosResponse<KibarItem>> =>
  api.get(`/api/kibar/${id}`);

export const createKibarItem = (data: any): Promise<AxiosResponse<KibarItem>> =>
  api.post('/api/kibar/', data);

export const updateKibarItem = (id: number, data: any): Promise<AxiosResponse<KibarItem>> =>
  api.patch(`/api/kibar/${id}`, data);

export const deleteKibarItem = (id: number): Promise<AxiosResponse<{ ok: boolean }>> =>
  api.delete(`/api/kibar/${id}`);

export const toggleKibarTrading = (
  id: number,
  enabled: boolean
): Promise<AxiosResponse<{ ok: boolean; trade_enabled: boolean }>> =>
  api.patch(`/api/kibar/${id}/trading`, { trade_enabled: enabled });

export const getKibarChartData = (id: number): Promise<AxiosResponse<any>> =>
  api.get(`/api/kibar/${id}/chart-data`);

export const previewKibarSL = (data: {
  trade_direction: string;
  support_resistance_level: number;
  price_target: number;
}): Promise<AxiosResponse<{ breakout_level: number; stop_loss: number; rr_ratio: number }>> =>
  api.post('/api/kibar/preview-sl', data);

export const getKibarPositions = (): Promise<AxiosResponse<any[]>> =>
  api.get('/api/kibar/positions');

export const closeKibarPosition = (data: {
  deal_id: string;
  epic: string;
  direction: string;
  size: number;
}): Promise<AxiosResponse<{ success: boolean; orders_cancelled: number }>> =>
  api.post('/api/kibar/close-position', data);

export const getKibarHistory = (params?: {
  page?: number;
  page_size?: number;
  days?: number;
  from_date?: string;
  to_date?: string;
  symbol?: string;
  direction?: string;
  outcome?: string;
  sort_by?: string;
  sort_dir?: string;
}): Promise<AxiosResponse<{ items: any[]; total: number }>> =>
  api.get('/api/kibar/history', { params });

export const getKibarHistoryStats = (params?: {
  days?: number;
  from_date?: string;
  to_date?: string;
  symbol?: string;
  direction?: string;
  outcome?: string;
}): Promise<AxiosResponse<any>> =>
  api.get('/api/kibar/history/stats', { params });

export default api;
